import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type WordCourse = {
  id: string;
  name: string;
  displayOrder: number;
  level: number;
  courseDescription?: string;
  totalLesson?: number;
  partsCount?: number;
};

const courseSchema = yup.object().shape({
  name: yup.string().required("コース名が必須です。"),
  displayOrder: yup.number().required("表示順が必須です。").min(1),
  level: yup.number().required("レベルが必須です。").min(1).max(5),
  courseDescription: yup.string(),
  totalLesson: yup.number().min(0),
});

const WordCourses = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const [courses, setCourses] = useState<WordCourse[]>([]);
  const [activeCourse, setActiveCourse] = useState<WordCourse | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(courseSchema),
  });

  const fetchCourses = async () => {
    dispatch(setIsLoading(true));
    try {
      const response = await API.get("word-courses");
      const { data } = response.data;
      
      // Handle both old format (direct array) and new format (with wrapper)
      let coursesData = [];
      
      if (data && Array.isArray(data)) {
        // New format with data wrapper
        coursesData = data;
      } else if (Array.isArray(response.data)) {
        // Old format (direct array)
        coursesData = response.data;
      }
      
      // Ensure all courses have required properties
      const processedCourses = (coursesData || []).map((course: any) => {
        if (!course) return null;
        
        return {
          id: course.id || "",
          name: course.name || course.courseName || "",
          displayOrder: course.displayOrder || 0,
          level: course.level || 1,
          courseDescription: course.courseDescription || course.description || "",
          totalLesson: course.totalLesson || 0,
          partsCount: course.partsCount || 0,
        };
      }).filter(Boolean); // Remove any null entries
      
      setCourses(processedCourses);
      // Make courses available globally for event handlers
      (window as any).courses = processedCourses;
      (window as any).openEditModal = openEditModal;
      (window as any).handleDeleteCourse = handleDeleteCourse;
    } catch (err) {
      console.error(err);
      toast.error("コースの取得に失敗しました。");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleCreateCourse = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      await API.post("word-course", data);
      toast.success("コースが作成されました。");
      fetchCourses();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCourse .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "コースの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateCourse = async (data: any) => {
    if (!activeCourse) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.put(`word-course/${activeCourse.id}`, data);
      toast.success("コースが更新されました。");
      fetchCourses();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCourse .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "コースの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteCourse = async (course: WordCourse) => {
    if (!window.confirm(`「${course.name}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`word-course/${course.id}`);
      toast.success("コースが削除されました。");
      fetchCourses();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "コースの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setActiveCourse(null);
    reset({
      name: "",
      displayOrder: courses.length + 1,
      level: 1,
      courseDescription: "",
      totalLesson: 0,
    });
  };

  const openEditModal = (course: WordCourse) => {
    setIsEditMode(true);
    setActiveCourse(course);
    reset(course);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  return (
    <>
      <Helmet>
        <title>単語コース管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title">単語コース管理</h4>
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#modalCourse"
                  onClick={openCreateModal}
                >
                  <i className="fas fa-plus me-2"></i>
                  新規作成
                </button>
              </div>
              <div className="card-body">
                <Loading />
                {!loading && (
                  <div className="table-responsive">
                    <DataTable
                      key={`word-courses-table-${courses.length}`}
                      data={courses}
                      className="display"
                      options={{
                        autoWidth: true,
                        columnDefs: [
                          { targets: "_all", className: "text-start" },
                        ],
                        stripeClasses: [],
                        order: [[1, "asc"]], // Sort by displayOrder
                        searching: true,
                        destroy: true,
                        info: true,
                        paging: true,
                        language: {
                          lengthMenu: "レコード数&nbsp;_MENU_",
                          emptyTable: "データがありません。",
                          search: "検索:",
                          paginate: {
                            first: "最初",
                            last: "最後",
                            next: "次へ",
                            previous: "前へ",
                          },
                        },
                      }}
                      columns={[
                        {
                          title: "コース名",
                          data: "name",
                          className: "text-start",
                          orderable: true,
                          render: (data, type, row) => {
                            return row.name || row.courseName || "-";
                          },
                        },
                        {
                          title: "表示順",
                          data: "displayOrder",
                          className: "text-center",
                          orderable: true,
                          render: (data) => data || 0,
                        },
                        {
                          title: "レベル",
                          data: "level",
                          className: "text-center",
                          orderable: true,
                          render: (data) => data || 1,
                        },
                        {
                          title: "パート数",
                          data: "partsCount",
                          className: "text-center",
                          orderable: true,
                          render: (data) => data || 0,
                        },
                        {
                          title: "総レッスン数",
                          data: "totalLesson",
                          className: "text-center",
                          orderable: true,
                          render: (data) => data || 0,
                        },
                        {
                          title: "説明",
                          data: null,
                          className: "text-start",
                          orderable: false,
                          render: (data, type, row) => {
                            return row.courseDescription || row.description || "-";
                          },
                        },
                        {
                          title: "操作",
                          data: null,
                          orderable: false,
                          className: "text-center",
                          render: (data, type, row) => {
                            return `
                              <div class="d-flex justify-content-center gap-2">
                                <button class="btn btn-sm btn-outline-primary edit-btn" data-id="${row.id || ''}">
                                  <i class="fas fa-edit"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger delete-btn" data-id="${row.id || ''}">
                                  <i class="fas fa-trash"></i>
                                </button>
                              </div>
                            `;
                          },
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          id="modalCourse"
          title={isEditMode ? "コース編集" : "コース作成"}
        >
          <Form onSubmit={handleSubmit(isEditMode ? handleUpdateCourse : handleCreateCourse)}>
            <Form.Group className="mb-3">
              <Form.Label>コース名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="コース名を入力"
                isInvalid={!!errors.name}
                {...register("name")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                isInvalid={!!errors.displayOrder}
                {...register("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>レベル <span className="text-danger">*</span></Form.Label>
              <Form.Select
                isInvalid={!!errors.level}
                {...register("level")}
              >
                <option value="1">1 - 初級</option>
                <option value="2">2 - 初中級</option>
                <option value="3">3 - 中級</option>
                <option value="4">4 - 中上級</option>
                <option value="5">5 - 上級</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.level?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>総レッスン数</Form.Label>
              <Form.Control
                type="number"
                min="0"
                isInvalid={!!errors.totalLesson}
                {...register("totalLesson")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.totalLesson?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="コースの説明を入力"
                {...register("courseDescription")}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
};

// Add event listeners for edit and delete buttons
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  
  if (target.closest(".edit-btn")) {
    const id = target.closest(".edit-btn")?.getAttribute("data-id");
    if (id) {
      // Navigate to course detail page
      window.location.href = `/content/word-courses/${id}`;
    }
  }
  
  if (target.closest(".delete-btn")) {
    const id = target.closest(".delete-btn")?.getAttribute("data-id");
    const course = (window as any).courses?.find((c: WordCourse) => c.id === id);
    if (course) {
      (window as any).handleDeleteCourse?.(course);
    }
  }
});

export default WordCourses;